import type { ReactElement } from 'react'
import type { SuggesterSectionKey } from './types'
import { SectionPinIcon, SectionStarIcon } from './figmaIcons'

/**
 * Inline `currentColor` SVG icons for `@metro/suggester`.
 *
 * `heart` / `clock` / `wifi-off` / `search` live here as inline SVGs.
 * In the Figma file these four are SF Symbol **glyphs** (PUA
 * code-points), NOT vector nodes — there is nothing to export. These
 * inline definitions are the agreed **cross-platform source of truth**
 * for the four glyphs (the Android session ported them verbatim);
 * `nearby` / `popular` / banner pin use the real Figma-exported assets
 * in `figmaIcons.tsx`.
 *
 * All icons are `currentColor` so host theme tokens drive their colour,
 * and `aria-hidden` (decorative — the section label / row text carries
 * the accessible name). Kept inline (no icon package) so the package
 * has zero runtime asset dependency, matching `route-card`.
 */

interface IconProps {
  /** Pixel size of the square icon box. */
  readonly size?: number
}

/** Heart icon — the `favorites` section header. */
export function HeartIcon({ size = 18 }: IconProps): ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" aria-hidden focusable="false">
      <path
        d="M9 15.3 3.4 9.9a3.4 3.4 0 0 1 4.8-4.8L9 5.9l.8-.8a3.4 3.4 0 0 1 4.8 4.8L9 15.3Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Clock icon — the `recents` section header. */
export function ClockIcon({ size = 18 }: IconProps): ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" aria-hidden focusable="false">
      <circle cx="9" cy="9" r="6.6" stroke="currentColor" strokeWidth="1.4" />
      <path d="M9 5v4.2l2.8 1.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}

/** Wifi-off icon — the offline notice. */
export function WifiOffIcon({ size = 18 }: IconProps): ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" aria-hidden focusable="false">
      <path
        d="M2.4 6.6A10 10 0 0 1 7 4.3M15.6 6.6a10 10 0 0 0-3-1.9M4.9 9.3A6.3 6.3 0 0 1 7.6 8M13.1 9.3a6.3 6.3 0 0 0-1.3-.8M7.3 12a2.6 2.6 0 0 1 3.1.3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <circle cx="9" cy="14.4" r="0.9" fill="currentColor" />
      <path d="M3 3 15 15" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}

/** Magnifying-glass icon — the search field and the empty state. */
export function SearchIcon({ size = 18 }: IconProps): ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" aria-hidden focusable="false">
      <circle cx="8" cy="8" r="5.4" stroke="currentColor" strokeWidth="1.6" />
      <path d="m12 12 3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

/**
 * Resolve the header icon for a {@link SuggesterSectionKey}.
 *
 * `nearby` / `popular` use the **real Figma-exported** assets
 * (`figmaIcons`); `favorites` / `recents` use the heart / clock inline
 * icons. `results` has NO section header in the Figma design (the
 * results state is a bare list) — the branch returns the search glyph
 * only as a defensive fallback and is not reached in practice.
 */
export function SectionIcon({ sectionKey }: { sectionKey: SuggesterSectionKey }): ReactElement {
  switch (sectionKey) {
    case 'nearby':
      return <SectionPinIcon />
    case 'favorites':
      return <HeartIcon />
    case 'recents':
      return <ClockIcon />
    case 'popular':
      return <SectionStarIcon />
    case 'results':
      return <SearchIcon />
  }
}
